import { ReactElement, useCallback, useMemo } from 'react';
import useFetchData from '../../../../../hooks/useFetchData';
import { ILawHistoricalData, buildHistoricalData } from './LawFirstLevelTable';
import GridItem from '../../../../layout/GridComponents/GridItem';
import CustomTable from '../../../../tables/CustomTable';
import {
  getMostRecentWeekday,
  getWeekdayXMonthsAgo,
} from '../../../../../utilities/dateUtilities';
import GeneralComponentErrorShield from '../../../../general/GeneralComponentErrorShield';
import { CustomColumn } from '../../../../../types/components/tables/tableTypes';
import { DatePicker } from '../../../../../types/redux/pages/PagesStore';
import { useAdditionalTitleBarDatePicker } from '../../../../../hooks/useAdditionalTitleBarComponents';

import {
  toggleHideStandardDatePicker,
  updateActiveDate,
  updateTitleBarSelectValue,
} from '../../../../../redux/pages/actions';
import { useDispatch, useSelector } from 'react-redux';
import {
  activeSectionSelector,
  createActiveFundSelectorBySection,
} from '../../../../../redux/pages/selectors';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLawHistoricalDataQuery } from '../../../../../api/rs-hg/risk/hooks/queries';
import type { LawHistoricalPayload } from '../../../../../api/rs-hg/risk/law.types';
import { QueryBoundary } from '../../../../utilityComponents/QueryBoundary';
import GenericStatusCell from '../../../../tables/GenericStatusCell';
import { specifyStatus } from '../../../../../utilities/generalMappings';

export interface HistoricalTableProps {
  activeFund: string;
  fetchUrl: string;
  reduxKey?: string;
}

export interface HistoricalTableNewProps {
  activeFund: string;
}

export default function HistoricalTable(
  props: HistoricalTableProps,
): ReactElement | null {
  const startDateWeekday = getWeekdayXMonthsAgo(1);
  const endDateWeekday = getMostRecentWeekday();

  const startDatePicker: DatePicker = {
    titleBarKey: 'startDate',
    titleBarTitle: 'Select Start Date: ',
    currentValue: dayjs(startDateWeekday).format('YYYY-MM-DD'),
    displayOnOverviewPage: false,
  };
  const { pathname } = useLocation();
  const endDatePicker: DatePicker = {
    titleBarKey: 'endDate',
    titleBarTitle: 'Select End Date: ',
    currentValue: dayjs(endDateWeekday).format('YYYY-MM-DD'),
    displayOnOverviewPage: false,
  };
  const activeSection = useSelector(activeSectionSelector);
  const activeFund = useSelector(
    createActiveFundSelectorBySection(activeSection),
  );

  const startDateValue = useAdditionalTitleBarDatePicker(startDatePicker);
  const endDateValue = useAdditionalTitleBarDatePicker(endDatePicker);

  const dispatch = useDispatch();

  // Hide the standard date picker when in historical mode
  useEffect(() => {
    dispatch(toggleHideStandardDatePicker(true));
    return () => {
      dispatch(toggleHideStandardDatePicker(false));
    };
  }, [dispatch]);

  const historical = useFetchData({
    url: `${props.fetchUrl}/${props.activeFund}/${startDateValue}/${endDateValue}`,
    keyName: `${props.activeFund
      }_ucits-law_historical_${startDateValue}-${endDateValue}_${props.reduxKey ? props.reduxKey : ''
      }`,
    makeFetch: !!startDateValue && !!endDateValue,
  });

  const navigateToPositionDate = useCallback(
    (date: string) => {
      dispatch(updateActiveDate(date));
      dispatch(updateTitleBarSelectValue('dataDisplay', 'current'));
    },
    [dispatch],
  );

  const historicalDataExtracted = historical ? historical.data : [];

  // create historical columns
  const [historicalTableData, historicalColumns] = useMemo(() => {
    return buildHistoricalData(
      historicalDataExtracted,
      pathname,
      activeFund?.id || 'na',
      navigateToPositionDate,
    );
  }, [historicalDataExtracted, pathname, activeFund, navigateToPositionDate]);

  const columns = historicalColumns?.map((col: any) => ({
    ...col,
    cellStyle: {
      ...col.cellStyle,
      padding: 0,
    },
    pdfRenderType: 'PageLinkButton',
  }));

  return (
    <GeneralComponentErrorShield dataObjects={[historical]}>
      <GridItem xs={12} card>
        <CustomTable<ILawHistoricalData>
          showToolbar={true}
          columns={columns}
          data={historicalTableData}
          csvFields={
            historicalColumns?.length > 0
              ? columns.map(
                (col: CustomColumn<ILawHistoricalData>) =>
                  col.field as string,
              )
              : undefined
          }
          toolbarComponents={{
            toolbarTitle: `${activeFund?.name} - Historical Data: ${startDateValue} to ${endDateValue}`,
          }}
          options={{
            paging: false,
            exportButton: true,
            search: true,
          }}
        />
      </GridItem>
    </GeneralComponentErrorShield>
  );
}


export function buildHistoricalDataFromModel(
  pageData: LawHistoricalPayload | undefined,
  pathname: string,
  fundId: string,
  cellHandler: (date: string) => void,
): [ILawHistoricalData[], CustomColumn<ILawHistoricalData>[]] {
  if (!pageData?.rules?.length || !pageData?.dates?.length) return [[], []];

  const { dates, rules, rule_data } = pageData;

  const baseColumns: CustomColumn<ILawHistoricalData>[] = [
    {
      title: '#',
      field: 'number',
      width: '1.5rem',
      maxWidth: '1.5rem',
      cellStyle: {
        padding: 0,
        textAlign: 'center',
      },
      headerStyle: { padding: 8, textAlign: 'center' },
    },
    {
      title: 'Rule',
      field: 'rule',
      cellStyle: {
        padding: 0,
        fontSize: 12,
        width: '18rem',
      },
      headerStyle: { textAlign: 'center' },
    },
  ];

  const tableData: ILawHistoricalData[] = rules.map((rule) => {
    const row: ILawHistoricalData = {
      number: rule.rule_number,
      rule: rule.description,
      assets: [],
    };
    dates.forEach((date) => {
      row[date] = rule_data?.[date]?.[rule.rule_key];
    });
    return row;
  });

  const dateColumns: CustomColumn<ILawHistoricalData>[] = dates.map(
    (date) => ({
      title: date,
      field: date,
      width: '3.12rem',
      render: (rowData: ILawHistoricalData) => (
        <GenericStatusCell
          status={specifyStatus(rowData[date])}
          height="2.25rem"
          fontSize={10}
          linkToPage={`${pathname}?&fundId=${fundId}&positionDate=${date}`}
          additionalHandler={() => cellHandler(date)}
        />
      ),
      cellStyle: {
        padding: 0,
        borderLeft: '1px solid white',
        textAlign: 'center',
      },
      headerStyle: {
        fontSize: 10,
        textAlign: 'center',
      },
    }),
  );

  return [tableData, [...baseColumns, ...dateColumns]];
}

export function HistoricalTableNew(
  props: HistoricalTableNewProps,
): ReactElement | null {
  const startDateWeekday = getWeekdayXMonthsAgo(1);
  const endDateWeekday = getMostRecentWeekday();

  const startDatePicker: DatePicker = {
    titleBarKey: 'startDate',
    titleBarTitle: 'Select Start Date: ',
    currentValue: dayjs(startDateWeekday).format('YYYY-MM-DD'),
    displayOnOverviewPage: false,
  };
  const { pathname } = useLocation();
  const endDatePicker: DatePicker = {
    titleBarKey: 'endDate',
    titleBarTitle: 'Select End Date: ',
    currentValue: dayjs(endDateWeekday).format('YYYY-MM-DD'),
    displayOnOverviewPage: false,
  };
  const activeSection = useSelector(activeSectionSelector);
  const activeFund = useSelector(
    createActiveFundSelectorBySection(activeSection),
  );

  const startDateValue = useAdditionalTitleBarDatePicker(startDatePicker);
  const endDateValue = useAdditionalTitleBarDatePicker(endDatePicker);

  const dispatch = useDispatch();

  // Hide the standard date picker when in historical mode
  useEffect(() => {
    dispatch(toggleHideStandardDatePicker(true));
    return () => {
      dispatch(toggleHideStandardDatePicker(false));
    };
  }, [dispatch]);

  const startDate = startDateValue ? String(startDateValue) : '';
  const endDate = endDateValue ? String(endDateValue) : '';

  const historicalQuery = useLawHistoricalDataQuery({
    fundName: props.activeFund,
    startDate,
    endDate,
  });

  const navigateToPositionDate = useCallback(
    (date: string) => {
      dispatch(updateActiveDate(date));
      dispatch(updateTitleBarSelectValue('dataDisplay', 'current'));
    },
    [dispatch],
  );

  const historicalData = historicalQuery.data?.data;

  // create historical columns
  const [historicalTableData, historicalColumns] = useMemo(() => {
    return buildHistoricalDataFromModel(
      historicalData,
      pathname,
      activeFund?.id || 'na',
      navigateToPositionDate,
    );
  }, [historicalData, pathname, activeFund, navigateToPositionDate]);

  const columns = historicalColumns?.map((col: any) => ({
    ...col,
    cellStyle: {
      ...col.cellStyle,
      padding: 0,
    },
    pdfRenderType: 'PageLinkButton',
  }));

  return (
    <QueryBoundary<LawHistoricalPayload>
      isLoading={historicalQuery.isLoading}
      isFetching={historicalQuery.isFetching}
      isError={historicalQuery.isError}
      error={historicalQuery.error}
      data={historicalData}
      loadingMessages={['Loading Historical Data...']}
      noDataMessage="No historical data available for this date range."
      errorMessage="Unable to load historical data."
    >
      {() => (
        <GridItem xs={12} card>
          <CustomTable<ILawHistoricalData>
            showToolbar={true}
            columns={columns}
            data={historicalTableData}
            csvFields={
              historicalColumns?.length > 0
                ? columns.map(
                  (col: CustomColumn<ILawHistoricalData>) =>
                    col.field as string,
                )
                : undefined
            }
            toolbarComponents={{
              toolbarTitle: `${activeFund?.name} - Historical Data: ${startDateValue} to ${endDateValue}`,
            }}
            options={{
              paging: false,
              exportButton: true,
              search: true,
            }}
          />
        </GridItem>
      )}
    </QueryBoundary>
  );
}
